import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material'
import {
  Announcement as AnnouncementIcon,
  PushPin as PushPinIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { announcementService, Announcement } from '../../services/announcementService'
import { AnnouncementDetailModal } from './AnnouncementDetailModal'

interface AnnouncementCardProps {
  userRole: 'CORPORATE_ADMIN' | 'USER' | 'SUPER_ADMIN'
  maxItems?: number
  variant?: 'default' | 'sidebar'
}

export const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  userRole,
  maxItems = 5,
  variant = 'default',
}) => {
  const isSidebar = variant === 'sidebar'
  const { t } = useTranslation('announcement')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const loadAnnouncements = useCallback(async () => {
    try {
      setLoading(true)
      const result = await announcementService.getActiveAnnouncements()
      // Limit to maxItems
      setAnnouncements(result.announcements.slice(0, maxItems))
    } catch (error) {
      console.error('Failed to load announcements:', error)
    } finally {
      setLoading(false)
    }
  }, [maxItems])

  const loadUnreadCount = useCallback(async () => {
    try {
      const result = await announcementService.getUnreadCount()
      setUnreadCount(result.count)
    } catch (error) {
      console.error('Failed to load unread count:', error)
    }
  }, [])

  useEffect(() => {
    loadAnnouncements()
    loadUnreadCount()
  }, [loadAnnouncements, loadUnreadCount])

  const handleOpenDetail = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
    setDetailOpen(true)
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setSelectedAnnouncement(null)
  }

  const handleMarkAsRead = useCallback(async (announcementId: string) => {
    try {
      await announcementService.markAsRead(announcementId)
      setUnreadCount((prev) => Math.max(0, prev - 1))
      // Update the announcement list to reflect read status
      setAnnouncements((prev) =>
        prev.map((a) => (a.id === announcementId ? { ...a, isRead: true } : a))
      )
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }, [])

  if (loading) {
    return (
      <Paper
        sx={{
          p: isSidebar ? 2 : 3,
          mb: isSidebar ? 0 : 3,
          ...(isSidebar && {
            position: 'sticky',
            top: 80,
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
          }),
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
          <CircularProgress size={24} />
        </Box>
      </Paper>
    )
  }

  if (announcements.length === 0) {
    if (isSidebar) {
      return (
        <Paper
          sx={{
            p: 2,
            position: 'sticky',
            top: 80,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AnnouncementIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">
              {t('dashboard.title')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.noAnnouncements')}
          </Typography>
        </Paper>
      )
    }
    return null
  }

  return (
    <>
      <Paper
        sx={{
          p: isSidebar ? 2 : 3,
          mb: isSidebar ? 0 : 3,
          ...(isSidebar && {
            position: 'sticky',
            top: 80,
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
          }),
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={isSidebar ? 1.5 : 2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AnnouncementIcon color="primary" fontSize={isSidebar ? 'small' : 'medium'} />
            <Typography variant={isSidebar ? 'subtitle1' : 'h6'} fontWeight="bold">
              {t('dashboard.title')}
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                color="primary"
                size="small"
                sx={{
                  ml: 0.5,
                  height: isSidebar ? 18 : 24,
                  fontSize: isSidebar ? '0.7rem' : '0.8rem',
                }}
              />
            )}
          </Box>
        </Box>

        <Stack spacing={isSidebar ? 1 : 2} divider={<Divider />}>
          {announcements.map((announcement) => (
            <Box
              key={announcement.id}
              sx={{
                cursor: 'pointer',
                py: isSidebar ? 0.5 : 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                borderRadius: 1,
                px: 1,
                mx: -1,
              }}
              onClick={() => handleOpenDetail(announcement)}
            >
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                {announcement.isPinned && (
                  <PushPinIcon sx={{ fontSize: isSidebar ? 14 : 18, mt: 0.25 }} color="primary" />
                )}
                {announcement.priority === 'HIGH' && (
                  <WarningIcon sx={{ fontSize: isSidebar ? 14 : 18, mt: 0.25 }} color="error" />
                )}
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <Typography
                      variant={isSidebar ? 'body2' : 'subtitle2'}
                      fontWeight={announcement.isRead ? 'normal' : 'bold'}
                      sx={{
                        color: announcement.isRead ? 'text.secondary' : 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isSidebar ? 'nowrap' : 'normal',
                        maxWidth: isSidebar ? '100%' : 'none',
                      }}
                    >
                      {announcement.title}
                    </Typography>
                    {!announcement.isRead && (
                      <Chip
                        label={t('dashboard.new')}
                        size="small"
                        color="error"
                        sx={{ height: 16, fontSize: '0.6rem', flexShrink: 0 }}
                      />
                    )}
                  </Box>
                  {!isSidebar && announcement.summary && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        mt: 0.5,
                      }}
                    >
                      {announcement.summary}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      display: 'block',
                      fontSize: isSidebar ? '0.65rem' : '0.75rem',
                    }}
                  >
                    {announcement.publishedAt
                      ? new Date(announcement.publishedAt).toLocaleDateString('ja-JP')
                      : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>

        <Box display="flex" justifyContent="center" mt={isSidebar ? 1 : 2}>
          <Button
            size="small"
            color="primary"
            sx={{ fontSize: isSidebar ? '0.75rem' : '0.875rem' }}
          >
            {t('dashboard.viewAll')}
          </Button>
        </Box>
      </Paper>

      <AnnouncementDetailModal
        open={detailOpen}
        announcement={selectedAnnouncement}
        onClose={handleCloseDetail}
        onMarkAsRead={handleMarkAsRead}
      />
    </>
  )
}
